import {
  Button,
  Calendar,
  CheckCircle2,
  Clock,
  Input,
  Modal,
} from "@ews-admin/global-design-system";
import { format, parseISO, startOfToday } from "date-fns";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { appointmentApi } from "../../../services/appointment.api";
import { doctorApi } from "../../../services/doctor.api";
import {
  Appointment,
  AppointmentStatusEnum,
} from "../../../types/appointment.definition";
import { TimeSlot } from "../../../types/doctor.definition";
import { ConfirmationModal } from "../ConfirmationModal/ConfirmationModal";
import { BookedAppointmentCard } from "./BookedAppointmentCard";

interface DeleteByDateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctorId: string;
  onDeleted?: () => void;
}

export const DeleteByDateRangeModal: React.FC<DeleteByDateRangeModalProps> = ({
  isOpen,
  onClose,
  doctorId,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogStep, setDialogStep] = useState<1 | 2>(1);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [bookedSlots, setBookedSlots] = useState<TimeSlot[]>([]);
  const [appointmentToCancel, setAppointmentToCancel] =
    useState<Appointment | null>(null);
  const [cancelling, setCancelling] = useState<boolean>(false);

  const handleClose = () => {
    setDialogStep(1);
    setStartDate("");
    setEndDate("");
    setBookedSlots([]);
    setAppointmentToCancel(null);
    onClose();
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel?.id) return;

    setCancelling(true);
    try {
      await appointmentApi.updateAppointment(appointmentToCancel.id, {
        ...appointmentToCancel,
        status: AppointmentStatusEnum.CANCELED,
      });
      // Reflect the new status on the listed slot without refetching.
      setBookedSlots((prev) =>
        prev.map((slot) => {
          const appt = slot.associatedAppointment;
          if (!appt || appt.id !== appointmentToCancel.id) return slot;
          return {
            ...slot,
            associatedAppointment: {
              ...appt,
              status: AppointmentStatusEnum.CANCELED,
            },
          };
        }),
      );
      toast.success(t("availability.appointmentCancelled"));
      // Cancelling frees the slot — refresh the availability list.
      onDeleted?.();
    } catch (err) {
      console.error("Failed to cancel appointment:", err);
      toast.error(t("availability.cancelAppointmentError"));
    } finally {
      setCancelling(false);
      setAppointmentToCancel(null);
    }
  };

  const handleDelete = async () => {
    if (!doctorId || !startDate || !endDate) return;

    setLoading(true);
    try {
      const remainingBooked = await doctorApi.deleteDoctorTimeSlotsByDateRange(
        doctorId,
        startDate,
        endDate,
      );
      setBookedSlots(remainingBooked);
      setDialogStep(2);
      onDeleted?.();
      toast.success(
        t(
          remainingBooked.length === 0
            ? "availability.deleteRangeSuccess"
            : "availability.deleteRangePartial",
        ),
      );
    } catch (err) {
      console.error("Failed to delete time slots by date range:", err);
      toast.error(t("availability.deleteRangeError"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("availability.deleteByDateRangeTitle")}
        variant="warning"
        size="4xl"
        closeOnOverlayClick={!loading}
      >
        <p className="mb-4 text-sm text-neutral-500">
          {t("availability.stepOf", {
            current: dialogStep,
            total: 2,
          })}
        </p>

        {/* Step indicator */}
        <div className="mb-6 flex justify-center">
          <div className="flex w-10/12 items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                dialogStep >= 1
                  ? "bg-primary-600 text-white"
                  : "bg-neutral-200 text-neutral-500"
              }`}
            >
              {dialogStep > 1 ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <span className="text-sm font-medium">1</span>
              )}
            </div>
            <div
              className={`h-1 flex-1 ${
                dialogStep >= 2 ? "bg-primary-600" : "bg-neutral-200"
              }`}
            ></div>
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                dialogStep >= 2
                  ? "bg-primary-600 text-white"
                  : "bg-neutral-200 text-neutral-500"
              }`}
            >
              <span className="text-sm font-medium">2</span>
            </div>
          </div>
        </div>

        {dialogStep === 1 ? (
          <div className="space-y-4">
            <p className="text-sm text-neutral-600">
              {t("availability.deleteByDateRangeDescription")}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label={t("availability.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={format(startOfToday(), "yyyy-MM-dd")}
                required
              />
              <Input
                label={t("availability.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || format(startOfToday(), "yyyy-MM-dd")}
                required
              />
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="error"
                onClick={handleDelete}
                disabled={!startDate || !endDate || loading}
                loading={loading}
              >
                {t("common.delete")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {bookedSlots.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="mb-3 h-12 w-12 text-green-500" />
                <p className="text-neutral-700">
                  {t("availability.allSlotsRemoved")}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-neutral-600">
                  {t("availability.bookedSlotsCannotDelete")}
                </p>
                <div className="max-h-96 space-y-4 overflow-y-auto">
                  {bookedSlots.map((slot) =>
                    slot.associatedAppointment ? (
                      <div
                        key={slot.id}
                        className="rounded-lg border border-amber-200"
                      >
                        <BookedAppointmentCard
                          appointment={slot.associatedAppointment}
                          onViewPatient={() =>
                            navigate(
                              `/patients/${slot.associatedAppointment?.patientId}`,
                            )
                          }
                          onCancel={() =>
                            setAppointmentToCancel(
                              slot.associatedAppointment ?? null,
                            )
                          }
                        />
                      </div>
                    ) : (
                      <div
                        key={slot.id}
                        className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                      >
                        <div className="flex items-center text-sm font-medium text-neutral-800">
                          <Calendar className="mr-2 h-4 w-4 text-amber-600" />
                          {format(parseISO(slot.slotDate), "EEEE, MMMM d, yyyy")}
                        </div>
                        <div className="mt-1 flex items-center text-sm text-neutral-600">
                          <Clock className="mr-2 h-4 w-4 text-amber-600" />
                          {slot.slotStartTime} - {slot.slotEndTime}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </>
            )}
            <div className="flex justify-end space-x-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDialogStep(1);
                  setBookedSlots([]);
                }}
              >
                {t("common.back")}
              </Button>
              <Button onClick={handleClose}>{t("common.close")}</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmationModal
        isOpen={appointmentToCancel !== null}
        title={t("availability.cancelAppointmentTitle")}
        message={t("availability.cancelAppointmentConfirm")}
        onConfirm={handleCancelAppointment}
        onCancel={() => setAppointmentToCancel(null)}
        variant="danger"
        isLoading={cancelling}
      />
    </>
  );
};

export default DeleteByDateRangeModal;
